import { put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import {
    GET_METER_READINGS,
    GET_METER_READINGS_SUCCESS,
    GET_METER_READINGS_FAILURE,
    GET_METER_READING_DETAILS,
    GET_METER_READING_DETAILS_SUCCESS,
    GET_METER_READING_DETAILS_FAILURE,
    GET_LAST_METER_READING,
    GET_LAST_METER_READING_SUCCESS,
    GET_LAST_METER_READING_FAILURE,
    CREATE_METER_READING,
    CREATE_METER_READING_SUCCESS,
    CREATE_METER_READING_FAILURE,
    CREATE_BULK_METER_READINGS,
    CREATE_BULK_METER_READINGS_SUCCESS,
    CREATE_BULK_METER_READINGS_FAILURE,
    UPLOAD_METER_READINGS,
    UPLOAD_METER_READINGS_SUCCESS,
    UPLOAD_METER_READINGS_FAILURE,
    DOWNLOAD_METER_READING_TEMPLATE,
    DOWNLOAD_METER_READING_TEMPLATE_SUCCESS,
    DOWNLOAD_METER_READING_TEMPLATE_FAILURE,
    UPDATE_METER_READING,
    UPDATE_METER_READING_SUCCESS,
    UPDATE_METER_READING_FAILURE,
    DELETE_METER_READING,
    DELETE_METER_READING_SUCCESS,
    DELETE_METER_READING_FAILURE
} from '../../../actions/index.types';
import {
    getMeterReadingsReq,
    getMeterReadingDetailsReq,
    getLastMeterReadingReq,
    createMeterReadingReq,
    createBulkMeterReadingsReq,
    uploadMeterReadingsReq,
    downloadMeterReadingTemplateReq,
    updateMeterReadingReq,
    deleteMeterReadingReq
} from '../../../../axios/request/SocietyPortal/MeterReadingReqs/MeterReadingReqs';

// Get All Meter Readings Saga
function* getMeterReadingsSaga(action) {
    try {
        const response = yield getMeterReadingsReq(action);
        if (response.success === true) {
            yield put({ type: GET_METER_READINGS_SUCCESS, data: response });
        } else {
            yield put({ type: GET_METER_READINGS_FAILURE, data: response.data });
            toast.error(response.data?.message || "Failed to fetch meter readings");
        }
    } catch (error) {
        yield put({ type: GET_METER_READINGS_FAILURE, data: error });
        toast.error(error?.response?.data?.message || "Something went wrong");
    }
}

// Get Meter Reading Details Saga
function* getMeterReadingDetailsSaga(action) {
    try {
        const response = yield getMeterReadingDetailsReq(action);
        if (response.success === true) {
            yield put({ type: GET_METER_READING_DETAILS_SUCCESS, data: response.data });
        } else {
            yield put({ type: GET_METER_READING_DETAILS_FAILURE, data: response.data });
            toast.error(response.data?.message || "Failed to fetch meter reading details");
        }
    } catch (error) {
        yield put({ type: GET_METER_READING_DETAILS_FAILURE, data: error });
        toast.error(error?.response?.data?.message || "Something went wrong");
    }
}

// Get Last Meter Reading Saga
function* getLastMeterReadingSaga(action) {
    try {
        const response = yield getLastMeterReadingReq(action);
        if (response.success === true) {
            yield put({ type: GET_LAST_METER_READING_SUCCESS, data: response.data });
            if (action?.data?.onSuccess) {
                action.data.onSuccess(response.data);
            }
        } else {
            yield put({ type: GET_LAST_METER_READING_FAILURE, data: response.data });
        }
    } catch (error) {
        yield put({ type: GET_LAST_METER_READING_FAILURE, data: error });
    }
}

// Create Meter Reading Saga
function* createMeterReadingSaga(action) {
    try {
        const response = yield createMeterReadingReq(action);
        if (response.success === true) {
            yield put({ type: CREATE_METER_READING_SUCCESS, data: response.data });
            toast.success(response.message || "Meter reading created successfully");
            if (action?.data?.onSuccess) {
                action.data.onSuccess(response.data);
            }
        } else {
            yield put({ type: CREATE_METER_READING_FAILURE, data: response.data });
            toast.error(response.message || "Failed to create meter reading");
        }
    } catch (error) {
        yield put({ type: CREATE_METER_READING_FAILURE, data: error });
        toast.error(error?.response?.data?.message || "Something went wrong");
    }
}

// Bulk Create Meter Readings Saga
function* createBulkMeterReadingsSaga(action) {
    try {
        const response = yield createBulkMeterReadingsReq(action);
        if (response.success === true) {
            yield put({ type: CREATE_BULK_METER_READINGS_SUCCESS, data: response.data });
            toast.success(response.message || "Meter readings created successfully");
            if (action?.data?.onSuccess) {
                action.data.onSuccess(response.data);
            }
        } else {
            yield put({ type: CREATE_BULK_METER_READINGS_FAILURE, data: response.data });
            toast.error(response.message || "Failed to create meter readings");
        }
    } catch (error) {
        yield put({ type: CREATE_BULK_METER_READINGS_FAILURE, data: error });
        toast.error(error?.response?.data?.message || "Something went wrong");
    }
}

// Upload Meter Readings via Excel Saga
function* uploadMeterReadingsSaga(action) {
    try {
        const response = yield uploadMeterReadingsReq(action);
        if (response.success === true) {
            yield put({ type: UPLOAD_METER_READINGS_SUCCESS, data: response.data });
            // A partly-accepted file still comes back as success, with the
            // rejected rows in data.errors - say so instead of a plain success.
            const failed = response.data?.failed || 0;
            if (failed > 0) {
                toast.warn(`${response.message || "Meter readings uploaded"}. ${failed} row(s) failed.`);
            } else {
                toast.success(response.message || "Meter readings uploaded successfully");
            }
            if (action?.data?.onSuccess) {
                action.data.onSuccess(response.data);
            }
        } else {
            yield put({ type: UPLOAD_METER_READINGS_FAILURE, data: response.data });
            toast.error(response.message || "Failed to upload meter readings");
            if (action?.data?.onError) {
                action.data.onError({ message: response.message, errors: [] });
            }
        }
    } catch (error) {
        yield put({ type: UPLOAD_METER_READINGS_FAILURE, data: error });
        const body = error?.response?.data;
        toast.error(body?.message || "Something went wrong");
        // When every row is rejected the API answers 400 with the per-row
        // reasons in errors.errors ({ row, flat_no, error }). Hand them to the
        // page - the toast alone only said "All readings failed to process".
        if (action?.data?.onError) {
            action.data.onError({
                message: body?.message || "Something went wrong",
                errors: Array.isArray(body?.errors?.errors) ? body.errors.errors : []
            });
        }
    }
}

// Download Meter Reading Template Saga
function* downloadMeterReadingTemplateSaga(action) {
    try {
        const response = yield downloadMeterReadingTemplateReq(action);
        if (response) {
            yield put({ type: DOWNLOAD_METER_READING_TEMPLATE_SUCCESS });
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `meter_reading_template.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Template downloaded successfully");
            if (action?.data?.onSuccess) {
                action.data.onSuccess();
            }
        } else {
            yield put({ type: DOWNLOAD_METER_READING_TEMPLATE_FAILURE });
            toast.error("Failed to download template");
        }
    } catch (error) {
        yield put({ type: DOWNLOAD_METER_READING_TEMPLATE_FAILURE, data: error });
        toast.error(error?.response?.data?.message || "Something went wrong");
    }
}

// Update Meter Reading Saga
function* updateMeterReadingSaga(action) {
    try {
        const response = yield updateMeterReadingReq(action);
        if (response.success === true) {
            yield put({ type: UPDATE_METER_READING_SUCCESS, data: response.data });
            toast.success(response.message || "Meter reading updated successfully");
            if (action?.data?.onSuccess) {
                action.data.onSuccess(response.data);
            }
        } else {
            yield put({ type: UPDATE_METER_READING_FAILURE, data: response.data });
            toast.error(response.message || "Failed to update meter reading");
        }
    } catch (error) {
        yield put({ type: UPDATE_METER_READING_FAILURE, data: error });
        toast.error(error?.response?.data?.message || "Something went wrong");
    }
}

// Delete Meter Reading Saga
function* deleteMeterReadingSaga(action) {
    try {
        const response = yield deleteMeterReadingReq(action);
        if (response.success === true) {
            yield put({ type: DELETE_METER_READING_SUCCESS, data: response.data });
            toast.success(response.message || "Meter reading deleted successfully");
            if (action?.data?.onSuccess) {
                action.data.onSuccess();
            }
        } else {
            yield put({ type: DELETE_METER_READING_FAILURE, data: response.data });
            toast.error(response.message || "Failed to delete meter reading");
        }
    } catch (error) {
        yield put({ type: DELETE_METER_READING_FAILURE, data: error });
        toast.error(error?.response?.data?.message || "Something went wrong");
    }
}

export default function* MeterReadingSaga() {
    yield takeLatest(GET_METER_READINGS, getMeterReadingsSaga);
    yield takeLatest(GET_METER_READING_DETAILS, getMeterReadingDetailsSaga);
    yield takeLatest(GET_LAST_METER_READING, getLastMeterReadingSaga);
    yield takeLatest(CREATE_METER_READING, createMeterReadingSaga);
    yield takeLatest(CREATE_BULK_METER_READINGS, createBulkMeterReadingsSaga);
    yield takeLatest(UPLOAD_METER_READINGS, uploadMeterReadingsSaga);
    yield takeLatest(DOWNLOAD_METER_READING_TEMPLATE, downloadMeterReadingTemplateSaga);
    yield takeLatest(UPDATE_METER_READING, updateMeterReadingSaga);
    yield takeLatest(DELETE_METER_READING, deleteMeterReadingSaga);
}
